/* eslint-disable react/no-did-update-set-state */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Col, Container, Input, Label, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import axios from 'axios';
import { UncontrolledTooltip } from 'reactstrap';

import { updateHouse } from '../../modules/house';
import { updateAuction, verifyAuction } from '../../modules/auction';
import { resetShippingReviewData } from '../../modules/shippingReview/thunks';
import InlineAlert from '../../utilities/form-components/InlineAlert';
import { isInvaluableSeller } from '../../utilities/BusinessLogic';
import { ahPath, hostServer, imagePath } from '../../utilities/UrlPaths';
import AddSale from './AddSale';
import AuctionGroup from './AuctionGroup';
import BuyNowGroup from './BuyNowGroup';
import Banner from './Banner';

import '../../index.scss';
import AuctionInfoModal from './AuctionInfoModal';

const errorLoadingText = `Unable to load your auctions / sales. Please try again later or contact
  Customer Care any time at:`;
const errorDeletingSessionText = `Unable to delete your session. Please try again later or contact
  Customer Care any time at:`;

class Auction extends Component {
  constructor(props) {
    super(props);
    document.title = 'InvaluableAuctions - Calendar';
    this.state = {
      catalogFilter: '',
      errorCatalogsExceeded: '',
      errorDeleting: false,
      errorLoading: false,
      isAddingSale: false,
      loading: true,
      loadingAuctions: false,
      expiredCard: false,
      overduePayment: false
    };
  }

  componentDidMount = () => {
    // (dyouberg) Need to use the actions from dispatchToProps, or auctions will not load
    const {
      dispatchUpdateAuction,
      dispatchUpdateHouse,
      dispatchVerifyAuction,
      dispatchResetShippingData,
      house: { houseRef },
      auction,
      match: {
        params: { type = 'current' }
      }
    } = this.props;

    dispatchResetShippingData();
    dispatchVerifyAuction(houseRef);

    if (auction?.housePaymentInfo?.length) {
      this.setState({
        expiredCard: auction.housePaymentInfo.expiredCard,
        overduePayment: auction.housePaymentInfo.overduePayment
      });
    }

    const calendar =
      type === 'current'
        ? [
            `/app/house/${houseRef}/auction-calendar?upcoming=true`,
            ({ data: { data } }) => dispatchUpdateAuction({ current: data })
          ]
        : [
            `/app/house/${houseRef}/auction-calendar?upcoming=false`,
            ({ data: { data } }) => dispatchUpdateAuction({ past: data })
          ];

    const processHouseData = ({ data: { data } }) => dispatchUpdateHouse(data);

    const requests = [
      [`/app/house/${houseRef}/auction-type/enabled`, processHouseData],
      [`/app/house/${houseRef}/paymentProcessorType`, processHouseData],
      [`/app/house/${houseRef}/paymentRequiredActivation`, processHouseData],
      calendar,
      [
        `/app/house/${houseRef}/bin/sale`,
        ({ data: { data } }) => dispatchUpdateHouse({ bin: data })
      ]
    ].map(([url, processData]) =>
      axios(url, {
        withCredentials: true,
        // This is no bueno
        headers: {
          Authorization: `${houseRef}*`
        }
      })
        .then(processData)
        .then(this.setState({ loading: false }))
        .catch(() => this.setState({ errorLoading: true }))
    );

    axios.all(requests).then(() => {
      this.setState({ loading: false });
    });
  };

  componentDidUpdate(prevProps) {
    const {
      match: {
        params: { type = 'current' }
      },
      auction
    } = this.props;

    if (get(prevProps, 'match.params.type') !== type) {
      this.fetchCalendar();
    }

    // change show modal state when auction housePaymentInfo expired card or overduePayment updates
    if (prevProps.auction !== this.props.auction) {
      this.setState({
        expiredCard: auction.housePaymentInfo?.expiredCard,
        overduePayment: auction.housePaymentInfo?.overduePayment
      });
    }

    return null;
  }

  fetchAuctions = async (auctionURL, auctionHeaders) => {
    this.setState({ loadingAuctions: true });
    try {
      const response = await axios.get(auctionURL, auctionHeaders);
      return response;
    } catch (error) {
      console.log(error);
    } finally {
      this.setState({ loadingAuctions: false });
    }
  };

  fetchCalendar = async (isDeletingSession = true) => {
    const {
      house: { houseRef },
      match: {
        params: { type = 'current' }
      },
      dispatchUpdateAuction
    } = this.props;

    try {
      const auctionURL = `/app/house/${houseRef}/auction-calendar?upcoming=${type.toLowerCase() ===
        'current'}`;
      const auctionHeaders = {
        withCredentials: true,
        // This is no bueno
        headers: {
          Authorization: `${houseRef}*`
        }
      };

      // When we are deleting a session - don't show the loading spinner
      if (!isDeletingSession) {
        this.setState({ loading: true });
      }

      // Fetch auction and handle loading state
      const auctions = await this.fetchAuctions(auctionURL, auctionHeaders);
      const data = get(auctions, 'data.data', null);

      if (type.toLowerCase() === 'current') {
        this.setState({ loading: false, isAddingSale: false });
        dispatchUpdateAuction({ current: data });
      } else {
        this.setState({ loading: false, isAddingSale: false });
        dispatchUpdateAuction({ past: data });
      }
    } catch {
      this.setState({ errorLoading: true });
    }
  };

  handleCatalogsExceeded = groupName => {
    window.scrollTo(0, 0);
    const errorCatalogsExceeded = `You have reached the maximum number of sessions allowed per sale for: ${groupName}. In order to create a new session, please first add a new sale.`;
    this.setState({ errorCatalogsExceeded });
  };

  handleDeleteSession = async catalogID => {
    try {
      await axios.delete(`/cps/app/catalog/${catalogID}`);
      this.fetchCalendar(true);
    } catch {
      this.setState({ errorDeleting: true });
    }
  };

  handleFilter = value => {
    this.props.history.push(`/my-auctions/${value}`);
  };

  toggleAddSale = value => {
    this.setState({ isAddingSale: value });
  };

  render() {
    const { errorLoading, loading } = this.state;

    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    // Could not load data from API
    if (errorLoading) {
      return (
        <Container className="py-4">
          <h1>{!isInvaluableSeller ? 'Auctions / Sales' : 'Sales'}</h1>

          <InlineAlert baseText={errorLoadingText} />
        </Container>
      );
    }

    const {
      auction,
      house,
      match: {
        params: { type = 'current' }
      },
      user: { userName: creator }
    } = this.props;

    const {
      bin,
      binEnabled,
      liveOrTimedEnabled,
      paymentProcessingEligible,
      paymentProcessingEnabled,
      paymentProcessingRequired,
    } = house;

    const { catalogFilter = '', errorCatalogsExceeded, errorDeleting, isAddingSale } = this.state;

    let data = get(
      auction,
      type === 'current' ? 'current.auctionCalendar' : 'past.auctionCalendar',
      []
    );

    const numAuctions = data.length;
    if (numAuctions > 0) {
      data = data.filter(x => {
        const { groupName } = x;
        if (groupName) {
          return x.groupName.match(new RegExp(catalogFilter, 'i')) || [];
        }

        return [];
      });
    }

    const dataExists = numAuctions > 0 || (binEnabled && Object.keys(bin).length > 1);

    const stealthMode =
      type === 'current'
        ? get(auction, 'current.stealthMode', false)
        : get(auction, 'past.stealthMode', false);
    const stealthModeAlertText =
      'Your account has been temporarily disabled. Please note this will affect your ability to post upcoming auctions. Please contact support for more details by calling:';

    const showGalleryWarning = 
      !stealthMode && !liveOrTimedEnabled && paymentProcessingEligible && !paymentProcessingEnabled;

    const galleryWarningUrl = (house['processor-type'] === 's') ? `${ahPath()}/ah/payments/enroll` : `${imagePath}/payments/signup`;

    // COR-1140 - disable both buttons if the condition below is true.
    const disableAddSaleAndSessionButton = (paymentProcessingRequired && !paymentProcessingEnabled);

    const cantAddSaleTooltipText = "Complete payment processing sign up to upload new sales.";

    return (
      <Container className="py-4">
        <Row>
          <Col xs={12}>
            <Banner
              ppEligible={paymentProcessingEligible}
              ppEnabled={paymentProcessingEnabled}
              ppRequired={paymentProcessingRequired}
              payrixEligible={house['payrix-eligible']}
              ppType={house['processor-type']}
            />
          </Col>
        </Row>
        <h1>{!isInvaluableSeller ? 'Auctions / Sales' : 'Sales'}</h1>

        {/* Not a 'breaking' delete scenario - just tell the user it didn't work */}
        {errorDeleting && <InlineAlert baseText={errorDeletingSessionText} />}
        {/* User exceeded the maximum number of sales per group */}
        {errorCatalogsExceeded && (
          <InlineAlert baseText={errorCatalogsExceeded} showPhones={false} />
        )}

        {type === 'current' &&
          !stealthMode &&
          dataExists &&
          paymentProcessingEligible &&
          paymentProcessingEnabled && (
            <Alert color="warning" className="my-4">
              Before publishing your sale, please make sure your terms are updated to include an
              accepted credit card for online payments.
            </Alert>
          )}

        {stealthMode && <InlineAlert baseText={stealthModeAlertText} />}

        {showGalleryWarning && (
          <Alert color="warning" className="my-4">
            You must have payment processing enabled to add a sale.{' '}
            <a href={galleryWarningUrl}>
              Sign up for payment processing now <FontAwesomeIcon icon="angle-right" />
            </a>
          </Alert>
        )}

        <Row>
          <Col xs={12} sm={9}>
            {type === 'current' ? (
              <p className="mb-0">
                To add a new sale, click &quot;+ Add Sale&quot;. <br />
                To add one or more sale days within each auction, click the &quot;+ Add
                Session&quot; link. <br />
              </p>
            ) : (
              <p className="mb-0">Past online auctions are automatically and permanently stored.</p>
            )}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${hostServer}/inv/help-center/resource/catalog/`}
            >
              Learn more about online auctions
            </a>
          </Col>

          {disableAddSaleAndSessionButton ? (
            <Col xs={12} sm={3} className="align-right">
              <span id={`disableSessionWarningIcon_${data.groupID}`} className="link-secondary px-2">
                  <FontAwesomeIcon className="color-danger" icon="exclamation-triangle" />
                  <UncontrolledTooltip
                    placement="left"
                    target={`disableSessionWarningIcon_${data.groupID}`}
                  >
                    {cantAddSaleTooltipText}
                  </UncontrolledTooltip>{' '}
                  Can&apos;t Add Sale
                </span>
              </Col>
          ) : (
            type === 'current' && (
              <Col xs={12} sm={3} className="align-right">
                {/* Note: ReactStrap uses the color attribute to determine the
                styles - with secondary being the default.  For instance this
                results in <button class="btn btn-primary"></button> */}
                <Button
                  color="primary"
                  className="cursor-not-allowed"
                  disabled={showGalleryWarning}
                  onClick={() => this.toggleAddSale(true)}
                >
                  <FontAwesomeIcon icon="plus" /> Add Sale
                </Button>
              </Col>
            )
          )}
        </Row>

        <div className="form-check form-check-inline mt-3 mb-3 mr-3">
          <Label check>
            <Input
              type="radio"
              name="upcomingPastRadio"
              className="space-right"
              checked={type === 'current'}
              onChange={() => this.handleFilter('current')}
            />{' '}
            Upcoming
          </Label>
        </div>
        <div className="form-check form-check-inline mb-3">
          <Label check>
            <Input
              type="radio"
              name="upcomingPastRadio"
              checked={type === 'past'}
              onChange={() => this.handleFilter('past')}
            />{' '}
            Past
          </Label>
        </div>

        {isAddingSale && (
          <AddSale
            creator={creator}
            fetchCalendar={this.fetchCalendar}
            house={house}
            toggleAddSale={this.toggleAddSale}
          />
        )}

        {!this.state.loadingAuctions && !dataExists && !isAddingSale && (
          <div className="mt-3">There are no {type === 'current' ? 'upcoming' : 'past'} sales.</div>
        )}

        {binEnabled && bin && bin.catalogID !== '' && (
          <Row>
            <Col xs={12}>
              <BuyNowGroup house={house} type={type} />
            </Col>
          </Row>
        )}

        {!this.state.loadingAuctions && (
          <AuctionInfoModal
            expiredCard={this.state.expiredCard}
            overduePayment={this.state.overduePayment}
          />
        )}

        <Row>
          <Col xs={12}>
            {this.state.loadingAuctions ? (
              <ReactLoading className="loading-spinner" type="spin" color="black" />
            ) : (
              data.map(d => (
                <AuctionGroup
                  handleCatalogsExceeded={this.handleCatalogsExceeded}
                  handleDeleteSession={this.handleDeleteSession}
                  house={house}
                  group={d}
                  type={type}
                  key={d.groupID}
                  disableAddSaleAndSessionButton={disableAddSaleAndSessionButton}
                />
              ))
            )}
          </Col>
        </Row>
      </Container>
    );
  }
}

const mapStateToProps = ({ user, auction, house, shippingReview }) => ({
  user,
  auction,
  house,
  shippingReview
});

const mapDispatchToProps = {
  dispatchUpdateHouse: updateHouse,
  dispatchUpdateAuction: updateAuction,
  dispatchVerifyAuction: verifyAuction,
  dispatchResetShippingData: resetShippingReviewData,
};

export default connect(mapStateToProps, mapDispatchToProps)(Auction);
